import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { ReactComponent as CheckIcon } from '@/assets/icons/done-circle.svg';
import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import { ActionsWrap, Td } from '@/components/Table';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { CoverImagesProjectResource } from '@/types/resources';
import { api } from '@/utils/api';
import {
  CoverImagesImageStatus,
  CoverImagesProjectStatus,
} from '@/utils/enums';

const StyledActionsDropdown = styled(ActionsDropdown)`
  width: 150px;
`;

const Tr = styled.tr`
  cursor: pointer;
  transition: background-color ease-out 0.2s;
  td {
    transition: background-color ease-out 0.2s;
  }
  &:hover {
    td {
      background-color: ${({ theme }) =>
        theme.colors.grayscaleExtraLight} !important;
    }
  }
`;

const Statutes = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StatusHolder = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StatusDot = styled.div<{ $status: CoverImagesImageStatus }>`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  ${({ theme, $status }) =>
    $status === CoverImagesImageStatus.PENDING &&
    css`
      background: ${theme.colors.warning};
    `}
  ${({ theme, $status }) =>
    $status === CoverImagesImageStatus.FIXING &&
    css`
      background: ${theme.colors.error};
    `}
    ${({ theme, $status }) =>
    $status === CoverImagesImageStatus.APPROVED &&
    css`
      background: ${theme.colors.success};
    `}
    ${({ theme, $status }) =>
    $status === CoverImagesImageStatus.SENT &&
    css`
      background: ${theme.colors.info};
    `}
`;

const StatusText = styled(Text)`
  span {
    color: ${({ theme }) => theme.colors.gray};
  }
`;

interface Props {
  project: CoverImagesProjectResource;
}

const CoverImagesTableRow: FC<Props> = ({ project }) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const handleRowClick = () => {
    navigate(`/general/cover-images/${project.id}`);
  };

  const onDelete = async () => {
    try {
      const confirmed = await alerts.confirm(
        'Are you sure you want to do delete this article?',
      );

      if (!confirmed) return;

      await api.delete(`/v1/cover-images/cover-images-projects/${project.id}`);

      await queryClient.invalidateQueries(['cover_images_projects']);

      alerts.success('Article has been successfully deleted');
    } catch (_) {
      alerts.error();
    }
  };

  const onMoveToDone = async () => {
    try {
      const confirmed = await alerts.confirm(
        'Are you sure you want to move this article to done?',
      );

      if (!confirmed) return;

      await api.post(`/v1/cover-images/end-article/${project.id}`);

      await queryClient.invalidateQueries(['cover_images_projects']);

      alerts.success('Article has been successfully moved to done');
    } catch (_) {
      alerts.error();
    }
  };

  return (
    <Tr onClick={handleRowClick}>
      <Td>{project.articleTitle}</Td>
      <Td>{DateTime.fromSQL(project.createdInBpAt).toRelative()}</Td>
      <Td>{DateTime.fromISO(project.updatedAt).toRelative()}</Td>
      <Td>{project.createdBy}</Td>
      <Td>{project.editor ?? '-'}</Td>
      <Td>
        <Statutes>
          {project.status === CoverImagesProjectStatus.OK ? (
            <StatusHolder>
              <StatusDot $status={CoverImagesImageStatus.APPROVED} />
              <StatusText $type="smallHeadline">
                Approved Covers: <span>{project.approvedCoversCount ?? 0}</span>
              </StatusText>
            </StatusHolder>
          ) : (
            <>
              <StatusHolder>
                <StatusDot $status={CoverImagesImageStatus.FIXING} />
                <StatusText $type="smallHeadline">
                  To Fix: <span>{project.fixingCoversCount ?? 0}</span>
                </StatusText>
              </StatusHolder>
              <StatusHolder>
                <StatusDot $status={CoverImagesImageStatus.PENDING} />
                <StatusText $type="smallHeadline">
                  To Review: <span>{project.pendingCoversCount ?? 0}</span>
                </StatusText>
              </StatusHolder>
              <StatusHolder>
                <StatusDot $status={CoverImagesImageStatus.APPROVED} />
                <StatusText $type="smallHeadline">
                  Approved: <span>{project.approvedCoversCount ?? 0}</span>
                </StatusText>
              </StatusHolder>
              <StatusHolder>
                <StatusDot $status={CoverImagesImageStatus.SENT} />
                <StatusText $type="smallHeadline">
                  Sent to BP: <span>{project.sentCoversCount ?? 0}</span>
                </StatusText>
              </StatusHolder>
            </>
          )}
        </Statutes>
      </Td>
      <Td $actionsCol onClick={(e) => e.stopPropagation()}>
        <ActionsWrap>
          <StyledActionsDropdown>
            {project.status !== CoverImagesProjectStatus.OK && (
              <ActionsDropdownButton as="button" onClick={onMoveToDone}>
                <span>
                  <CheckIcon />
                </span>
                Move to Done
              </ActionsDropdownButton>
            )}
            <ActionsDropdownButton as="button" onClick={onDelete}>
              <span>
                <TrashIcon />
              </span>
              Delete
            </ActionsDropdownButton>
          </StyledActionsDropdown>
        </ActionsWrap>
      </Td>
    </Tr>
  );
};

export default CoverImagesTableRow;
